import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Send, Paperclip, X } from "lucide-react";

const SERVICES = [
  "Video Production (Filming & Editing)",
  "Filming Only",
  "Photography",
  "Video Editing",
  "Drone",
  "Animation / Motion Graphics",
  "White Label / Agency",
];

export default function QuoteForm({ heading = "GET A QUOTE WITHIN 4 HOURS", compact = false }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [briefFile, setBriefFile] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", company: "", service: SERVICES[0], message: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onBriefChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      alert("Please attach a file under 15MB. For larger briefs, email it directly.");
      e.target.value = "";
      return;
    }
    setBriefFile(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      if (briefFile) body.append("brief", briefFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: "POST",
        body,
      });
      const data = await res.json().catch(() => ({ ok: false }));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Something went wrong, please try again or call us.");
      }
      navigate("/thank-you");
    } catch (err) {
      setErrorMsg(err.message || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white/[0.03] border border-white/10 rounded-md p-6 md:p-8"
      data-testid="quote-form"
    >
      {heading && <h3 className="yv-h2 text-white mb-2">{heading}</h3>}
      {!compact && (
        <p className="text-white/60 mb-6 text-sm">
          Fill out the form and we'll send pricing, our production process and details on how to claim FREE photography with your video.
        </p>
      )}

      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <div>
          <label className="yv-label">Your Name</label>
          <input name="name" required value={form.name} onChange={onChange} className="yv-input" data-testid="form-name" />
        </div>
        <div>
          <label className="yv-label">Phone</label>
          <input name="phone" value={form.phone} onChange={onChange} className="yv-input" data-testid="form-phone" />
        </div>
        <div>
          <label className="yv-label">Email</label>
          <input name="email" type="email" required value={form.email} onChange={onChange} className="yv-input" data-testid="form-email" />
        </div>
        <div>
          <label className="yv-label">Company</label>
          <input name="company" value={form.company} onChange={onChange} className="yv-input" data-testid="form-company" />
        </div>
        <div className="md:col-span-2">
          <label className="yv-label">Service</label>
          <select name="service" value={form.service} onChange={onChange} className="yv-select" data-testid="form-service">
            {SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="yv-label">Tell us about your project</label>
          <textarea
            name="message"
            value={form.message}
            onChange={onChange}
            className="yv-textarea"
            placeholder="Include location, number of filming days, how many videos/edits you need, deadlines, examples you want to emulate..."
            data-testid="form-message"
          />
        </div>
        <div className="md:col-span-2">
          <label className="yv-label">Have a brief? Attach it (optional)</label>
          {briefFile ? (
            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-white/5 border border-[var(--yellow)]/40" data-testid="form-brief-attached">
              <div className="flex items-center gap-3 text-sm text-white/85 truncate">
                <Paperclip size={16} className="text-[var(--yellow)] flex-shrink-0" />
                <span className="truncate">{briefFile.name}</span>
                <span className="text-white/40 text-xs flex-shrink-0">{(briefFile.size / 1024).toFixed(0)} KB</span>
              </div>
              <button
                type="button"
                onClick={() => setBriefFile(null)}
                className="text-white/60 hover:text-[var(--yellow)] p-1"
                aria-label="Remove attached brief"
                data-testid="form-brief-remove"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label
              className="inline-flex items-center gap-2 px-4 py-3 rounded-md border border-dashed border-white/20 hover:border-[var(--yellow)] hover:bg-white/5 text-white/80 text-sm cursor-pointer transition w-full sm:w-auto"
              data-testid="form-brief-button"
            >
              <Paperclip size={16} className="text-[var(--yellow)]" />
              Upload a brief (PDF, DOC, image, max 15MB)
              <input
                type="file"
                className="sr-only"
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.heic"
                onChange={onBriefChange}
                data-testid="form-brief-input"
              />
            </label>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="yv-btn yv-btn-primary mt-6 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="form-submit"
      >
        {submitting ? "Sending..." : (
          <>
            <Send size={16} /> Send me prices
          </>
        )}
      </button>
      {errorMsg && (
        <p className="text-red-400 text-sm mt-3 text-center" data-testid="form-error">{errorMsg}</p>
      )}

      <p className="text-white/40 text-xs mt-4 text-center">
        We take data protection seriously. Your details will never be shared outside of Yellow.
      </p>
    </form>
  );
}
